import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import Swal from 'sweetalert2';
import { motion } from 'framer-motion';

const API = process.env.REACT_APP_API_URL || '/api';

const container = { hidden: { opacity: 0 }, show: { opacity: 1, transition: { staggerChildren: 0.04 } } };
const fadeIn = { hidden: { opacity: 0, y: 8 }, show: { opacity: 1, y: 0, transition: { duration: 0.25 } } };

const Input = (p) => <input {...p} className="w-full text-xs bg-surface-50 dark:bg-surface-700 border border-surface-200 dark:border-surface-600 rounded-xl px-3.5 py-2.5 text-surface-700 dark:text-surface-300 focus:outline-none focus:ring-2 focus:ring-emerald-500/20" />;
const Textarea = (p) => <textarea {...p} className="w-full text-xs bg-surface-50 dark:bg-surface-700 border border-surface-200 dark:border-surface-600 rounded-xl px-3.5 py-2.5 text-surface-700 dark:text-surface-300 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 resize-none" />;
const Select = (p) => <select {...p} className="w-full text-xs bg-surface-50 dark:bg-surface-700 border border-surface-200 dark:border-surface-600 rounded-xl px-3.5 py-2.5 text-surface-700 dark:text-surface-300 focus:outline-none focus:ring-2 focus:ring-emerald-500/20" />;
const Label = ({ children }) => <label className="flex flex-col gap-1 text-[11px] font-medium text-surface-400">{children}</label>;

const ImageUpload = ({ value, onChange, label }) => {
  const fileRef = useRef(null);
  const handleFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => onChange(ev.target.result);
    reader.readAsDataURL(file);
  };
  return (
    <Label>
      {label}
      <input type="file" accept="image/*" onChange={handleFile} ref={fileRef}
        className="text-xs text-surface-400 file:mr-3 file:py-1.5 file:px-3 file:rounded-xl file:border-0 file:text-[11px] file:font-semibold file:bg-emerald-50 dark:file:bg-emerald-900/20 file:text-emerald-600 dark:file:text-emerald-400" />
      {value && <img src={value} alt="preview" className="w-full max-h-36 object-contain rounded-xl bg-surface-50 dark:bg-surface-700 mt-1" />}
    </Label>
  );
};

const TabBtn = ({ active, onClick, children }) => (
  <button onClick={onClick}
    className={`px-3 py-1.5 text-[10px] font-semibold rounded-xl whitespace-nowrap transition-all ${active ? 'bg-emerald-500 text-white shadow-md shadow-emerald-500/20' : 'bg-surface-100 dark:bg-surface-700 text-surface-500 dark:text-surface-400 hover:bg-surface-200 dark:hover:bg-surface-600'}`}>
    {children}
  </button>
);

export default function SiteSettings() {
  const { token } = useAuth();
  const h = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
  const [tab, setTab] = useState('general');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [settings, setSettings] = useState(null);
  const [sliders, setSliders] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [socials, setSocials] = useState([]);
  const [hours, setHours] = useState([]);
  const [testimonials, setTestimonials] = useState([]);

  const fetchJson = async (url) => { try { const r = await fetch(url, { headers: h }); return r.ok ? await r.json() : null; } catch { return null; } };

  const fetchAll = async () => {
    setLoading(true);
    const [s, sl, c, so, hh, t] = await Promise.all([
      fetchJson(`${API}/sitesettings`),
      fetchJson(`${API}/homesliders/all`),
      fetchJson(`${API}/campaigns/all`),
      fetchJson(`${API}/socialmedia`),
      fetchJson(`${API}/workinghours`),
      fetchJson(`${API}/testimonials/all`),
    ]);
    setSettings(s || {});
    setSliders(sl || []);
    setCampaigns(c || []);
    setSocials(so || []);
    setHours(hh || []);
    setTestimonials(t || []);
    setLoading(false);
  };

  useEffect(() => { fetchAll(); }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  const saveSettings = async () => {
    setSaving(true);
    try {
      const r = await fetch(`${API}/sitesettings`, { method: 'PUT', headers: h, body: JSON.stringify(settings) });
      if (r.ok) { Swal.fire({ icon: 'success', title: 'Kaydedildi!', timer: 1500, showConfirmButton: false }); }
      else { throw new Error('Kaydetme başarısız'); }
    } catch (e) { Swal.fire({ icon: 'error', title: 'Hata', text: e.message }); }
    setSaving(false);
  };

  const saveItem = async (endpoint, item, idKey = 'id') => {
    try {
      if (item[idKey]) await fetch(`${API}/${endpoint}/${item[idKey]}`, { method: 'PUT', headers: h, body: JSON.stringify(item) });
      else await fetch(`${API}/${endpoint}`, { method: 'POST', headers: h, body: JSON.stringify(item) });
      await fetchAll();
      Swal.fire({ icon: 'success', title: 'Kaydedildi!', timer: 1200, showConfirmButton: false });
    } catch (e) { Swal.fire({ icon: 'error', title: 'Hata', text: e.message }); }
  };

  const deleteItem = async (endpoint, id) => {
    const res = await Swal.fire({ title: 'Emin misiniz?', text: 'Bu öğeyi silmek istediğinize emin misiniz?', icon: 'warning', showCancelButton: true, confirmButtonText: 'Evet, Sil!', cancelButtonText: 'İptal', confirmButtonColor: '#ef4444' });
    if (!res.isConfirmed) return;
    try {
      await fetch(`${API}/${endpoint}/${id}`, { method: 'DELETE', headers: h });
      await fetchAll();
      Swal.fire({ icon: 'success', title: 'Silindi!', timer: 1200, showConfirmButton: false });
    } catch (e) { Swal.fire({ icon: 'error', title: 'Hata', text: e.message }); }
  };

  const set = (key, val) => setSettings(p => ({ ...p, [key]: val }));
  const setArr = (setter, idx, key, val) => setter(prev => prev.map((item, i) => i === idx ? { ...item, [key]: val } : item));

  const TABS = [
    { key: 'general', label: 'Genel' },
    { key: 'hero', label: 'Hero' },
    { key: 'slider', label: 'Slider' },
    { key: 'campaigns', label: 'Kampanyalar' },
    { key: 'about', label: 'Hakkımızda' },
    { key: 'social', label: 'Sosyal Medya' },
    { key: 'hours', label: 'Çalışma Saatleri' },
    { key: 'testimonials', label: 'Yorumlar' },
    { key: 'theme', label: 'Tema' },
    { key: 'seo', label: 'SEO' },
  ];

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="flex flex-col items-center gap-3">
        <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
        <span className="text-xs text-surface-400">İçerik yükleniyor...</span>
      </div>
    </div>
  );

  const renderSection = (label, children) => (
    <motion.div variants={fadeIn} className="bg-white dark:bg-surface-800 rounded-2xl border border-surface-100 dark:border-surface-700 shadow-soft p-5">
      <h3 className="font-display font-bold text-surface-800 dark:text-white text-sm mb-4">{label}</h3>
      <div className="space-y-3">{children}</div>
    </motion.div>
  );

  const renderMultiItem = (items, fields, endpoint, setter, title) => (
    <div className="space-y-3">
      <button onClick={() => {
        const empty = {};
        fields.forEach(f => empty[f.key] = f.default || '');
        setter(prev => [...prev, empty]);
      }}
        className="px-3.5 py-1.5 bg-emerald-500 hover:bg-emerald-600 text-white text-[11px] font-semibold rounded-xl shadow-lg shadow-emerald-500/20 transition-all text-xs">
        + Yeni {title}
      </button>
      {items.length === 0 && (
        <div className="text-center py-8 text-xs text-surface-400 bg-surface-50 dark:bg-surface-700/30 rounded-xl">
          Henüz {title.toLowerCase()} eklenmemiş
        </div>
      )}
      {items.map((item, idx) => (
        <div key={idx} className="bg-surface-50 dark:bg-surface-700/30 rounded-xl p-4 space-y-2.5 border border-surface-100 dark:border-surface-700">
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-semibold text-surface-400 uppercase">#{idx + 1}</span>
            <button onClick={() => deleteItem(endpoint, item.id)}
              className="px-2 py-1 text-[9px] font-semibold bg-red-50 dark:bg-red-900/20 text-red-500 dark:text-red-400 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors">Sil</button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
            {fields.map(f => (
              f.type === 'image' ? (
                <div key={f.key} className={f.fullWidth ? 'sm:col-span-2' : ''}>
                  <ImageUpload value={item[f.key]} onChange={v => setArr(setter, idx, f.key, v)} label={f.label} />
                </div>
              ) : f.type === 'select' ? (
                <Label key={f.key}>{f.label}<Select value={item[f.key] || ''} onChange={e => setArr(setter, idx, f.key, f.parse ? f.parse(e.target.value) : e.target.value)}>{f.options.map(o => <option key={o} value={o}>{o}</option>)}</Select></Label>
              ) : f.type === 'number' ? (
                <Label key={f.key}>{f.label}<Input type="number" value={item[f.key] ?? ''} onChange={e => setArr(setter, idx, f.key, f.parse ? f.parse(e.target.value) : e.target.value)} /></Label>
              ) : f.type === 'textarea' ? (
                <Label key={f.key}><span>{f.label}</span><Textarea rows={2} value={item[f.key] || ''} onChange={e => setArr(setter, idx, f.key, e.target.value)} /></Label>
              ) : f.type === 'checkbox' ? (
                <label key={f.key} className="flex items-center gap-2.5 text-[11px] font-medium text-surface-400"><input type="checkbox" checked={!!item[f.key]} onChange={e => setArr(setter, idx, f.key, e.target.checked)} className="w-4 h-4 rounded border-surface-300 dark:border-surface-600 text-emerald-500 focus:ring-emerald-500/20" />{f.label}</label>
              ) : (
                <Label key={f.key}>{f.label}<Input value={item[f.key] || ''} onChange={e => setArr(setter, idx, f.key, e.target.value)} /></Label>
              )
            ))}
          </div>
          <button onClick={() => saveItem(endpoint, item)}
            className="w-full py-2 text-[10px] font-semibold bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl transition-all active:scale-[0.98]">
            Kaydet
          </button>
        </div>
      ))}
    </div>
  );

  return (
    <motion.div variants={container} initial="hidden" animate="show" className="space-y-4">
      <div className="flex flex-wrap gap-1.5 overflow-x-auto pb-1">
        {TABS.map(t => (
          <TabBtn key={t.key} active={tab === t.key} onClick={() => setTab(t.key)}>{t.label}</TabBtn>
        ))}
      </div>

      {/* GENEL */}
      {tab === 'general' && (
        <motion.div variants={fadeIn} className="space-y-4">
          {renderSection('İşletme Bilgileri', (
            <>
              <Label>İşletme Adı<Input value={settings.siteName || ''} onChange={e => set('siteName', e.target.value)} required /></Label>
              <Label>Açıklama<Textarea rows={2} value={settings.description || ''} onChange={e => set('description', e.target.value)} /></Label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <ImageUpload value={settings.logo} onChange={v => set('logo', v)} label="Logo" />
                <ImageUpload value={settings.favicon} onChange={v => set('favicon', v)} label="Favicon" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Label>Telefon<Input value={settings.phone || ''} onChange={e => set('phone', e.target.value)} /></Label>
                <Label>Email<Input type="email" value={settings.email || ''} onChange={e => set('email', e.target.value)} /></Label>
              </div>
              <Label>Adres<Textarea rows={2} value={settings.address || ''} onChange={e => set('address', e.target.value)} /></Label>
              <Label>Google Maps Linki<Input value={settings.googleMapsLink || ''} onChange={e => set('googleMapsLink', e.target.value)} placeholder="https://maps.google.com/?q=..." /></Label>
              <Label>Footer Açıklaması<Textarea rows={2} value={settings.footerDescription || ''} onChange={e => set('footerDescription', e.target.value)} /></Label>
              {/* Copyright metni sabittir, admin tarafından değiştirilemez */}
            </>
          ))}
          <button onClick={saveSettings} disabled={saving}
            className="w-full py-2.5 text-xs font-semibold bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl shadow-lg shadow-emerald-500/20 transition-all active:scale-[0.98] disabled:opacity-50">
            {saving ? 'Kaydediliyor...' : 'Değişiklikleri Kaydet'}
          </button>
        </motion.div>
      )}

      {/* HERO */}
      {tab === 'hero' && (
        <motion.div variants={fadeIn} className="space-y-4">
          {renderSection('Hero / Banner Alanı', (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Label>Büyük Başlık<Input value={settings.heroTitle || ''} onChange={e => set('heroTitle', e.target.value)} placeholder="Her Anın Tadı Güzel Olsun" /></Label>
                <Label>CTA Buton Yazısı<Input value={settings.ctaText || ''} onChange={e => set('ctaText', e.target.value)} placeholder="Menüyü Görüntüle" /></Label>
              </div>
              <Label>Alt Açıklama<Textarea rows={2} value={settings.heroSubtitle || ''} onChange={e => set('heroSubtitle', e.target.value)} /></Label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Label>CTA Buton Linki<Input value={settings.ctaLink || ''} onChange={e => set('ctaLink', e.target.value)} placeholder="/menu" /></Label>
              </div>
              <ImageUpload value={settings.heroImage} onChange={v => set('heroImage', v)} label="Hero Görseli (1920x800 önerilir)" />
            </>
          ))}
          <button onClick={saveSettings} disabled={saving}
            className="w-full py-2.5 text-xs font-semibold bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl shadow-lg shadow-emerald-500/20 transition-all active:scale-[0.98] disabled:opacity-50">
            {saving ? 'Kaydediliyor...' : 'Değişiklikleri Kaydet'}
          </button>
        </motion.div>
      )}

      {/* SLIDER */}
      {tab === 'slider' && (
        <motion.div variants={fadeIn} className="space-y-4">
          {renderSection('Slider Yönetimi', renderMultiItem(sliders, [
            { key: 'title', label: 'Başlık' },
            { key: 'description', label: 'Açıklama' },
            { key: 'sortOrder', label: 'Sıralama', type: 'number', parse: parseInt, default: 1 },
            { key: 'isActive', label: 'Aktif', type: 'checkbox', default: true },
            { key: 'image', label: 'Görsel', type: 'image' },
          ], 'homesliders', setSliders, 'Slider'))}
        </motion.div>
      )}

      {/* KAMPANYALAR */}
      {tab === 'campaigns' && (
        <motion.div variants={fadeIn} className="space-y-4">
          {renderSection('Kampanyalar', renderMultiItem(campaigns, [
            { key: 'title', label: 'Kampanya Başlığı' },
            { key: 'description', label: 'Açıklama' },
            { key: 'discountRate', label: 'İndirim Oranı (%)', type: 'number', parse: parseInt },
            { key: 'isActive', label: 'Aktif', type: 'checkbox', default: true },
            { key: 'image', label: 'Kampanya Görseli', type: 'image' },
          ], 'campaigns', setCampaigns, 'Kampanya'))}
        </motion.div>
      )}

      {/* HAKKIMIZDA */}
      {tab === 'about' && (
        <motion.div variants={fadeIn} className="space-y-4">
          {renderSection('Hakkımızda', (
            <>
              <Label>Başlık<Input value={settings.aboutTitle || ''} onChange={e => set('aboutTitle', e.target.value)} placeholder="Hakkımızda" /></Label>
              <Label>Açıklama<Textarea rows={4} value={settings.aboutDescription || ''} onChange={e => set('aboutDescription', e.target.value)} /></Label>
              <ImageUpload value={settings.aboutImage} onChange={v => set('aboutImage', v)} label="Görsel" />
            </>
          ))}
          <button onClick={saveSettings} disabled={saving}
            className="w-full py-2.5 text-xs font-semibold bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl shadow-lg shadow-emerald-500/20 transition-all active:scale-[0.98] disabled:opacity-50">
            {saving ? 'Kaydediliyor...' : 'Değişiklikleri Kaydet'}
          </button>
        </motion.div>
      )}

      {/* SOSYAL MEDYA */}
      {tab === 'social' && (
        <motion.div variants={fadeIn} className="space-y-4">
          {renderSection('Sosyal Medya Linkleri', renderMultiItem(socials, [
            { key: 'platform', label: 'Platform', type: 'select', options: ['Instagram', 'Facebook', 'WhatsApp', 'TikTok', 'X (Twitter)', 'YouTube', 'LinkedIn', 'Pinterest'] },
            { key: 'url', label: 'URL / Link' },
            { key: 'icon', label: 'İkon Adı' },
            { key: 'sortOrder', label: 'Sıralama', type: 'number', parse: parseInt, default: 1 },
          ], 'socialmedia', setSocials, 'Sosyal Medya'))}
        </motion.div>
      )}

      {/* ÇALIŞMA SAATLERİ */}
      {tab === 'hours' && (
        <motion.div variants={fadeIn} className="space-y-4">
          {renderSection('Çalışma Saatleri', renderMultiItem(hours, [
            { key: 'day', label: 'Gün', type: 'select', options: ['Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi', 'Pazar'] },
            { key: 'openingTime', label: 'Açılış Saati', default: '09:00' },
            { key: 'closingTime', label: 'Kapanış Saati', default: '23:00' },
            { key: 'isClosed', label: 'Kapalı', type: 'checkbox' },
            { key: 'sortOrder', label: 'Sıralama', type: 'number', parse: parseInt, default: 1 },
          ], 'workinghours', setHours, 'Çalışma Saati'))}
        </motion.div>
      )}

      {/* YORUMLAR */}
      {tab === 'testimonials' && (
        <motion.div variants={fadeIn} className="space-y-4">
          {renderSection('Müşteri Yorumları', renderMultiItem(testimonials, [
            { key: 'authorName', label: 'Ad Soyad' },
            { key: 'content', label: 'Yorum', type: 'textarea' },
            { key: 'rating', label: 'Puan (1-5)', type: 'number', parse: parseInt, default: 5 },
            { key: 'isActive', label: 'Aktif', type: 'checkbox', default: true },
            { key: 'authorImage', label: 'Profil Görseli', type: 'image' },
          ], 'testimonials', setTestimonials, 'Yorum'))}
        </motion.div>
      )}

      {/* TEMA */}
      {tab === 'theme' && (
        <motion.div variants={fadeIn} className="space-y-4">
          {renderSection('Tema Ayarları', (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Label>Ana Renk<Input type="color" value={settings.themePrimaryColor || '#10b981'} onChange={e => set('themePrimaryColor', e.target.value)} className="h-10 cursor-pointer" /></Label>
                <Label>İkincil Renk<Input type="color" value={settings.themeSecondaryColor || '#3b82f6'} onChange={e => set('themeSecondaryColor', e.target.value)} className="h-10 cursor-pointer" /></Label>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Label>Buton Rengi<Input type="color" value={settings.themeButtonColor || '#10b981'} onChange={e => set('themeButtonColor', e.target.value)} className="h-10 cursor-pointer" /></Label>
                <label className="flex items-center gap-2.5 text-[11px] font-medium text-surface-400 pt-6">
                  <input type="checkbox" checked={!!settings.darkMode} onChange={e => set('darkMode', e.target.checked)}
                    className="w-4 h-4 rounded border-surface-300 dark:border-surface-600 text-emerald-500 focus:ring-emerald-500/20" />
                  Varsayılan Koyu Mod
                </label>
              </div>
              <div className="mt-4 p-4 rounded-xl bg-surface-50 dark:bg-surface-700/30 border border-surface-100 dark:border-surface-700">
                <p className="text-[10px] font-semibold text-surface-400 uppercase tracking-wider mb-2">Önizleme</p>
                <div className="flex gap-2">
                  <div className="w-8 h-8 rounded-lg" style={{ backgroundColor: settings.themePrimaryColor || '#10b981' }} />
                  <div className="w-8 h-8 rounded-lg" style={{ backgroundColor: settings.themeSecondaryColor || '#3b82f6' }} />
                  <div className="w-8 h-8 rounded-lg" style={{ backgroundColor: settings.themeButtonColor || '#10b981' }} />
                </div>
              </div>
            </>
          ))}
          <button onClick={saveSettings} disabled={saving}
            className="w-full py-2.5 text-xs font-semibold bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl shadow-lg shadow-emerald-500/20 transition-all active:scale-[0.98] disabled:opacity-50">
            {saving ? 'Kaydediliyor...' : 'Değişiklikleri Kaydet'}
          </button>
        </motion.div>
      )}

      {/* SEO */}
      {tab === 'seo' && (
        <motion.div variants={fadeIn} className="space-y-4">
          {renderSection('SEO Ayarları', (
            <>
              <Label>Meta Başlığı<Input value={settings.metaTitle || ''} onChange={e => set('metaTitle', e.target.value)} placeholder="NEŞVE Cafe | Lezzetli Yemekler ve Taze Kahveler" /></Label>
              <Label>Meta Açıklaması<Textarea rows={2} value={settings.metaDescription || ''} onChange={e => set('metaDescription', e.target.value)} /></Label>
              <ImageUpload value={settings.ogImage} onChange={v => set('ogImage', v)} label="OpenGraph Görseli (Facebook/Twitter paylaşım görseli)" />
            </>
          ))}
          <button onClick={saveSettings} disabled={saving}
            className="w-full py-2.5 text-xs font-semibold bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl shadow-lg shadow-emerald-500/20 transition-all active:scale-[0.98] disabled:opacity-50">
            {saving ? 'Kaydediliyor...' : 'Değişiklikleri Kaydet'}
          </button>
        </motion.div>
      )}
    </motion.div>
  );
}
